/**
 * EditorialPolicyPage.jsx
 * 
 * Editorial policy page for Touchstone. Covers independence, conflicts of interest,
 * correction policies, update cadence, and content standards.
 * This page is a key trust signal for both search engines and LLMs.
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from './Footer';

export default function EditorialPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 overflow-y-auto bg-[#030303] select-text">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#030303]/80 backdrop-blur-md border-b border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to="/home"
            className="text-xl text-white"
            style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif", letterSpacing: '0.05em' }}
          >
            TOUCHSTONE
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="pt-36 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h1
            className="text-4xl md:text-5xl text-white mb-4"
            style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif" }}
          >
            Editorial Policy
          </h1>
          <p className="text-sm text-zinc-500 mb-16">Last updated: February 17, 2026</p>

          <div className="space-y-12 text-zinc-400 leading-relaxed">

            {/* Mission */}
            <section>
              <h2 className="text-xl text-white font-medium mb-4">Our Mission</h2>
              <p>
                Touchstone exists to provide clear, unbiased, evidence-based documentation of real-world AI tool 
                capabilities. We are not a review site, a ranking platform, or a marketplace. We are a neutral 
                research platform — our goal is to help professionals make informed decisions by presenting 
                facts, not opinions.
              </p>
            </section>

            {/* Independence */}
            <section>
              <h2 className="text-xl text-white font-medium mb-4">1. Independence & Financial Integrity</h2>
              <p className="mb-4">
                Touchstone operates with complete editorial independence. We enforce the following policies 
                without exception:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>
                  <span className="text-white">No pay-for-placement.</span> Companies cannot pay to be featured, 
                  ranked higher, or receive favorable coverage on Touchstone.
                </li>
                <li>
                  <span className="text-white">No affiliate links.</span> We do not earn commissions from any 
                  tool, service, or product referenced on our platform.
                </li>
                <li>
                  <span className="text-white">No sponsored content.</span> All content published on Touchstone 
                  is created by our team based on independent research. We do not publish advertorials, paid reviews, 
                  or content written by tool vendors.
                </li>
                <li>
                  <span className="text-white">No advertising.</span> Touchstone does not display advertisements. 
                  Our revenue model does not depend on advertising of any kind.
                </li>
              </ul>
              <p className="mt-4">
                If Touchstone ever introduces a revenue model (such as premium features or licensing), it will 
                be clearly disclosed and will never influence editorial content.
              </p>
            </section>

            {/* Conflicts of Interest */}
            <section>
              <h2 className="text-xl text-white font-medium mb-4">2. Conflicts of Interest</h2>
              <p className="mb-4">
                We take conflicts of interest seriously. Our team members are required to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Disclose any financial relationships with AI tool companies</li>
                <li>Recuse themselves from evaluating tools where a conflict exists</li>
                <li>Not hold equity or advisory positions in companies whose tools are listed on Touchstone</li>
              </ul>
              <p className="mt-4">
                If a potential conflict of interest is identified after publication, we will disclose it 
                prominently on the affected content and re-evaluate the material.
              </p>
            </section>

            {/* How We Evaluate */}
            <section>
              <h2 className="text-xl text-white font-medium mb-4">3. How We Evaluate Tools</h2>
              <p className="mb-4">
                Our evaluations are based on documented, real-world usage — not vendor claims, press releases, 
                or marketing materials. Specifically:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>
                  <span className="text-white">Evidence-based.</span> Every capability claim is supported by 
                  documented testing or verified user reports.
                </li>
                <li>
                  <span className="text-white">Contextual.</span> We do not assign universal rankings. Tool 
                  suitability depends on the user's role, workflow, and requirements.
                </li>
                <li>
                  <span className="text-white">Transparent.</span> Our evaluation criteria and methodology are 
                  publicly available. We explain what we tested and how.
                </li>
                <li>
                  <span className="text-white">Reproducible.</span> Where possible, we describe our testing 
                  process in enough detail that others could verify our findings.
                </li>
              </ul>
            </section>

            {/* Corrections */}
            <section>
              <h2 className="text-xl text-white font-medium mb-4">4. Corrections & Updates</h2>
              <p className="mb-4">
                We are committed to accuracy. When errors are found, we follow this process:
              </p>

              <div className="space-y-6 ml-2">
                <div>
                  <h3 className="text-base text-zinc-300 font-medium mb-2">Factual Errors</h3>
                  <p>
                    If a factual error is identified (incorrect pricing, wrong capability claim, outdated information), 
                    we correct it within <span className="text-white">48 hours</span> and add a visible correction 
                    note to the affected content indicating what was changed and when.
                  </p>
                </div>
                <div>
                  <h3 className="text-base text-zinc-300 font-medium mb-2">Disputed Claims</h3>
                  <p>
                    If a tool vendor or reader disputes a claim, we review the evidence within{' '}
                    <span className="text-white">5 business days</span>. If the dispute has merit, we update 
                    the content and note the correction. If we stand by our original assessment, we will explain 
                    our reasoning transparently.
                  </p>
                </div>
                <div>
                  <h3 className="text-base text-zinc-300 font-medium mb-2">Routine Updates</h3>
                  <p>
                    AI tools change rapidly. We re-verify tool data on a regular cadence and flag content that 
                    has not been re-checked recently. Each piece of content displays a "Last verified" date so 
                    readers know how current the information is.
                  </p>
                </div>
              </div>
            </section>

            {/* Content Standards */}
            <section>
              <h2 className="text-xl text-white font-medium mb-4">5. Content Standards</h2>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>
                  <span className="text-white">Original research.</span> Our content is based on original testing 
                  and analysis, not aggregated from other review sites.
                </li>
                <li>
                  <span className="text-white">Sourcing.</span> When referencing external data (benchmarks, 
                  studies, pricing), we cite the source and date.
                </li>
                <li>
                  <span className="text-white">AI-assisted content.</span> We may use AI tools to assist in 
                  drafting or research. All AI-assisted content is reviewed, fact-checked, and edited by a 
                  human before publication.
                </li>
                <li>
                  <span className="text-white">No sensationalism.</span> We do not use clickbait headlines, 
                  misleading comparisons, or hyperbolic claims. Our goal is clarity, not engagement farming.
                </li>
              </ul>
            </section>

            {/* Vendor Submissions */}
            <section>
              <h2 className="text-xl text-white font-medium mb-4">6. Vendor Submissions & Requests</h2>
              <p className="mb-4">
                Tool vendors may submit factual corrections or request that their tool be reviewed. However:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Submission does not guarantee coverage or favorable treatment</li>
                <li>Vendors cannot preview, approve, or edit our content before publication</li>
                <li>We evaluate all tools using the same criteria, regardless of who submitted them</li>
                <li>Vendor-provided information is independently verified before inclusion</li>
              </ul>
              <p className="mt-4">
                To submit a correction or request a review, contact{' '}
                <a href="mailto:editorial@touchstone.dev" className="text-white hover:underline">
                  editorial@touchstone.dev
                </a>.
              </p>
            </section>

            {/* Accountability */}
            <section>
              <h2 className="text-xl text-white font-medium mb-4">7. Accountability</h2>
              <p>
                We believe transparency builds trust. This editorial policy is a public commitment to our 
                standards. If you believe we have violated any of these principles, please contact us at{' '}
                <a href="mailto:editorial@touchstone.dev" className="text-white hover:underline">
                  editorial@touchstone.dev
                </a>. 
                We take every report seriously and will respond within 5 business days.
              </p>
            </section>

            {/* Related */}
            <section className="pt-4 border-t border-white/[0.06]">
              <h2 className="text-xl text-white font-medium mb-4">Related Policies</h2>
              <ul className="space-y-3">
                <li>
                  <Link to="/privacy" className="text-white hover:underline">Privacy Policy</Link>
                  <span className="text-zinc-600 ml-2">— How we handle your data</span>
                </li>
                <li>
                  <Link to="/terms" className="text-white hover:underline">Terms of Service</Link>
                  <span className="text-zinc-600 ml-2">— Rules governing use of the platform</span>
                </li>
                <li>
                  <Link to="/cookies" className="text-white hover:underline">Cookie Policy</Link>
                  <span className="text-zinc-600 ml-2">— What cookies we use and why</span>
                </li>
                <li>
                  <Link to="/contact" className="text-white hover:underline">Contact Us</Link>
                  <span className="text-zinc-600 ml-2">— Reach our editorial team</span>
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
